import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  useMediaQuery,
} from "@mui/material";

interface Props {
  /**
   * Re-fires `useTenantFromHost`. Returns a promise that settles once the
   * underlying query has resolved (success or error). The component does
   * not branch on the result — the parent <LoginPage> re-renders into
   * §State 1 → §State 2 on success, or stays on this card on repeat failure.
   */
  refetch: () => Promise<unknown>;
}

/**
 * UX brief §State 6 + Addendum 2026-05-04 — Network-error retry card.
 *
 * Renders when `useTenantFromHost` resolves to a non-404 error (5xx,
 * network failure, timeout). Manual-only retry via the "Try again" button.
 * Auto-retry is intentionally NOT layered on top of TanStack Query's
 * built-in `retry: failureCount < 1` — see addendum §"Auto-retry".
 *
 * Focus management: the Try again button receives focus on first paint
 * (NOT the alert) so Enter recovers without tabbing. The alert keeps
 * `role="alert"` for assertive announcement.
 *
 * Reduced-motion: `useMediaQuery('(prefers-reduced-motion: reduce)')` →
 * the spinner is replaced by a static "Trying again…" text label.
 * Disabled state alone signals in-flight; no pulse, no shimmer.
 */
export function NetworkErrorRetryCard({ refetch }: Props) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  // Focus the recovery action on first mount so keyboard users (and SR
  // users after the role="alert" announcement) can press Enter to recover
  // without tabbing through the page.
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  async function handleRetry() {
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      // On success the parent unmounts this card before this state lands;
      // on repeat failure the state matters so the button re-enables.
      setIsRetrying(false);
    }
  }

  return (
    <Stack spacing={2} sx={{ width: "100%", maxWidth: 480 }}>
      <Alert severity="error" role="alert" variant="outlined">
        <AlertTitle>Couldn&apos;t reach the sign-in service</AlertTitle>
        Check your internet connection and try again. If the problem persists,
        contact your tenant admin.
      </Alert>
      <Box>
        <Button
          ref={buttonRef}
          variant="contained"
          fullWidth
          color="primary"
          onClick={handleRetry}
          disabled={isRetrying}
          aria-busy={isRetrying}
          sx={{ minHeight: 44 }}
        >
          {isRetrying ? (
            prefersReducedMotion ? (
              // Reduced-motion fallback: static text label, no spinner.
              // Disabled state alone signals in-flight (no pulse, no shimmer).
              "Trying again…"
            ) : (
              <>
                {/*
                  aria-hidden on the spinner so the button's accessible name
                  is the single string "Trying again…" rather than a fused
                  composite of a nested role="progressbar" + the text label.
                  The button's own aria-busy="true" carries the live status
                  to AT users.
                */}
                <CircularProgress
                  size={20}
                  aria-hidden="true"
                  sx={{ mr: 1, color: "inherit" }}
                />
                Trying again…
              </>
            )
          ) : (
            "Try again"
          )}
        </Button>
      </Box>
    </Stack>
  );
}
